import { Link, useLocation } from "wouter";
import {
  HomeIcon,
  UsersIcon,
  SettingsIcon,
  BuildingIcon,
  WalletIcon,
  ReceiptIcon,
  ArrowRightLeftIcon,
  BarChart3Icon,
  BellIcon,
  CircleSlashIcon,
  FileBoxIcon,
  FolderTreeIcon,
  LandmarkIcon,
  LogOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShieldIcon,
} from "lucide-react";
import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";
import { authClient } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CerebellumMark } from "@/components/CerebellumMark";
import { useSidebar } from "@/context/SidebarContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type NavItem = {
  path: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  adminOnly: boolean;
};

const MAIN_ITEMS: NavItem[] = [
  { path: "/", label: "Home", icon: HomeIcon, adminOnly: false },
  { path: "/properties", label: "Properties", icon: BuildingIcon, adminOnly: false },
  { path: "/reserves", label: "Reserves", icon: WalletIcon, adminOnly: false },
  { path: "/distributions", label: "Distributions", icon: ArrowRightLeftIcon, adminOnly: false },
  { path: "/reports", label: "Reports", icon: BarChart3Icon, adminOnly: false },
  { path: "/sunk-cost-pool", label: "Sunk Cost Pool", icon: CircleSlashIcon, adminOnly: false },
  { path: "/tasks", label: "Tasks", icon: BellIcon, adminOnly: false },
];

const ADMIN_ITEMS: NavItem[] = [
  { path: "/draw-requests", label: "Draw Requests", icon: ReceiptIcon, adminOnly: false },
  { path: "/users", label: "Users", icon: UsersIcon, adminOnly: true },
  { path: "/admin/templates", label: "Templates", icon: FileBoxIcon, adminOnly: true },
  {
    path: "/admin/document-types",
    label: "Document Types",
    icon: FolderTreeIcon,
    adminOnly: true,
  },
  {
    path: "/admin/promissory-notes",
    label: "Promissory Notes",
    icon: LandmarkIcon,
    adminOnly: true,
  },
];

// Sub-project #6 closes this out — every prior placeholder is now a
// real surface. Kept as an empty array so the JSX guard renders
// nothing rather than throwing on a missing const.
const COMING_SOON: { label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [];

const AUTO_COLLAPSE_MS = 10_000;

export function DesktopSidebar() {
  const [loc] = useLocation();
  const { data: user } = useSession();
  const { collapsed, setCollapsed } = useSidebar();
  const [hovered, setHovered] = useState(false);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email;
  const visibleAdminItems = ADMIN_ITEMS.filter((i) => !i.adminOnly || user?.isAdmin);

  useEffect(() => {
    if (collapsed || hovered) return;
    const t = setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_MS);
    return () => clearTimeout(t);
  }, [collapsed, hovered, setCollapsed]);

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={0} disableHoverableContent>
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          "fixed inset-y-0 left-0 z-[1100] flex flex-col border-r border-border bg-card/50",
          "transition-[width] duration-500 ease-in-out",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <div
          className={cn(
            "flex items-center border-b border-border py-5",
            collapsed ? "justify-center px-3" : "justify-between px-5",
          )}
        >
          {!collapsed && (
            <div className="flex min-w-0 items-center gap-2">
              <CerebellumMark className="size-6 shrink-0 text-[color:var(--accent)]" />
              <span className="truncate font-display text-lg font-semibold tracking-[-0.04em]">
                Cerebellum
                <span
                  aria-hidden
                  className="ml-[6px] inline-block size-[5px] translate-y-[-2px] rounded-full bg-[color:var(--accent)]"
                />
              </span>
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
                onClick={() => setCollapsed(!collapsed)}
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
              >
                {collapsed ? (
                  <ChevronRightIcon className="size-4" />
                ) : (
                  <ChevronLeftIcon className="size-4" />
                )}
              </button>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">Expand navigation</TooltipContent>}
          </Tooltip>
        </div>
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3">
          <NavList items={MAIN_ITEMS} loc={loc} collapsed={collapsed} />
          {visibleAdminItems.length > 0 && (
            <NavSection
              title="Administration"
              icon={ShieldIcon}
              collapsed={collapsed}
              className="mt-6"
            >
              <NavList items={visibleAdminItems} loc={loc} collapsed={collapsed} />
            </NavSection>
          )}
          {COMING_SOON.length > 0 && (
            <NavSection title="Coming soon" collapsed={collapsed} className="mt-6">
              <ul className="space-y-1">
                {COMING_SOON.map((i) => {
                  const Icon = i.icon;
                  return (
                    <li key={i.label}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              "flex cursor-not-allowed items-center gap-3 rounded-md py-2 text-sm text-muted-foreground/60",
                              collapsed ? "justify-center px-2" : "px-3",
                            )}
                          >
                            <Icon className="size-4 shrink-0" />
                            {!collapsed && <span className="truncate">{i.label}</span>}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {collapsed ? `${i.label} — arrives in a future sub-project` : "Arrives in a future sub-project"}
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            </NavSection>
          )}
        </nav>
        <div
          className={cn(
            "flex items-center gap-2 border-t border-border py-3",
            collapsed ? "flex-col px-2" : "justify-between px-3",
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                href="/settings"
                aria-label={displayName ? `Profile — ${displayName}` : "Profile"}
                className={cn(
                  "flex min-w-0 items-center gap-2 rounded-md text-xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
                  loc.startsWith("/settings") && "text-foreground",
                  collapsed ? "size-8 justify-center" : "flex-1 px-2 py-1.5",
                )}
              >
                <SettingsIcon className="size-4 shrink-0" />
                {!collapsed && (
                  <span className="truncate" title={displayName ?? undefined}>
                    {displayName ?? "—"}
                  </span>
                )}
              </Link>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right">{displayName ? `Profile — ${displayName}` : "Profile"}</TooltipContent>
            )}
          </Tooltip>
          <NotificationBell collapsed={collapsed} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Sign out"
                onClick={() => {
                  authClient.signOut().then(() => {
                    window.location.href = "/login";
                  });
                }}
              >
                <LogOutIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">Sign out</TooltipContent>}
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
}

function NavList({
  items,
  loc,
  collapsed,
}: {
  items: NavItem[];
  loc: string;
  collapsed: boolean;
}) {
  return (
    <ul className="space-y-1">
      {items.map((i) => {
        const Icon = i.icon;
        const isActive = loc === i.path || (i.path !== "/" && loc.startsWith(i.path));
        return (
          <li key={i.path}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  href={i.path}
                  className={cn(
                    "flex items-center gap-3 rounded-md py-2 text-sm transition-colors",
                    collapsed ? "justify-center px-2" : "px-3",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                  )}
                >
                  <Icon className="size-4 shrink-0" />
                  {!collapsed && <span className="truncate">{i.label}</span>}
                </Link>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right">{i.label}</TooltipContent>}
            </Tooltip>
          </li>
        );
      })}
    </ul>
  );
}

function NavSection({
  title,
  icon: Icon,
  collapsed,
  className,
  children,
}: {
  title: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  collapsed: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("border-t border-border pt-3", className)}>
      {collapsed ? (
        <div className="mb-2 flex justify-center" aria-label={title} title={title}>
          {Icon ? (
            <Icon className="size-3 text-muted-foreground/60" />
          ) : (
            <span className="block h-px w-6 bg-border" aria-hidden />
          )}
        </div>
      ) : (
        <p className="px-3 pb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}
